import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, BarChart3, Ticket as TicketIcon, History, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../ui/button';
import { ActivityFeedItem } from '../activity/ActivityFeedItem';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABEL = {
  pending_approval: 'Pending',
  access_granted: 'Granted',
  used: 'Used',
  revoked: 'Revoked',
};

const STATUS_CLASS = {
  pending_approval: 'text-[#FFB800] border-[#FFB800]/30 bg-[#FFB800]/10',
  access_granted: 'text-[#D4AF37] border-[#D4AF37]/30 bg-[#D4AF37]/10',
  used: 'text-[#6EE7B7] border-[#6EE7B7]/30 bg-[#6EE7B7]/10',
  revoked: 'text-[#C44545] border-[#C44545]/30 bg-[#C44545]/10',
};

function fmt(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso.slice(0, 16); }
}

export default function EventDetailView() {
  const { event_id } = useParams();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const token = localStorage.getItem('session_token');
      const qs = new URLSearchParams();
      qs.set('event_id', event_id);
      ['from', 'to', 'status'].forEach(k => { if (search.get(k)) qs.set(k, search.get(k)); });
      const res = await axios.get(`${API}/reports/event-detail?${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load event detail');
    } finally { setLoading(false); }
  }, [event_id, search]);

  useEffect(() => { load(); }, [load]);

  const summary = data?.summary;

  return (
    <div className="min-h-screen bg-[#050505] text-[#EAE7E1]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-[#AFAFAF] hover:text-[#D4AF37] h-9 px-2"
            data-testid="event-detail-back"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Reports
          </Button>
          <BarChart3 className="w-5 h-5 text-[#D4AF37]" />
          <h1 className="text-xl font-semibold tracking-tight truncate" data-testid="event-detail-title">
            {data?.event?.event_name || 'Event'}
          </h1>
        </div>

        {error && (
          <div className="bg-[#C44545]/10 border border-[#C44545]/30 rounded-sm p-4 mb-6 text-sm text-[#C44545]" data-testid="event-detail-error">
            {error}
          </div>
        )}

        {/* KPI block */}
        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="event-detail-kpis">
            <Kpi label="Total Passes" value={summary.total_passes} testId="ed-kpi-passes" />
            <Kpi label="Total Attendees" value={summary.total_attendees} testId="ed-kpi-attendees" />
            <Kpi label="Checked-In %" value={`${summary.checked_in_percent}%`} testId="ed-kpi-checkin-pct" />
            <Kpi label="Revoked" value={summary.revoked_count} testId="ed-kpi-revoked" accent="#C44545" />
          </div>
        )}

        {/* Ticket table */}
        <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm overflow-x-auto" data-testid="event-detail-table">
          <table className="min-w-full text-sm">
            <thead className="border-b border-[#1A1A1A]">
              <tr className="text-left">
                <Th>Guest</Th>
                <Th>Email</Th>
                <Th>Tier</Th>
                <Th className="text-right">Attendees</Th>
                <Th>Status</Th>
                <Th>Issued</Th>
                <Th>Checked-In</Th>
                <Th>Revoked</Th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-xs text-[#555]" data-testid="event-detail-loading">Loading…</td></tr>
              )}
              {!loading && (data?.tickets || []).length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-xs text-[#555]" data-testid="event-detail-empty">
                  <TicketIcon className="w-6 h-6 mx-auto mb-2 text-[#333]" />
                  No tickets match these filters
                </td></tr>
              )}
              {!loading && (data?.tickets || []).map(t => (
                <tr
                  key={t.ticket_id}
                  className="border-b border-[#111] hover:bg-[#0D0D0D] transition-colors"
                  data-testid={`event-detail-row-${t.ticket_id}`}
                >
                  <td className="px-4 py-3">
                    <div className="text-[#EAE7E1]">{t.guest_name || '—'}</div>
                    {t.is_comp && <div className="text-[10px] text-[#C6A66B] uppercase tracking-wider">Comp</div>}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => navigate(`/reports/guest/${encodeURIComponent(t.guest_email)}`)}
                      className="text-[#C6A66B] hover:text-[#D4AF37] underline-offset-2 hover:underline truncate max-w-[200px] inline-block"
                      title={t.guest_email}
                      data-testid={`event-detail-email-${t.ticket_id}`}
                    >
                      {t.guest_email}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-[#AFAFAF]">{t.tier_name || '—'}</td>
                  <td className="px-4 py-3 text-right text-[#EAE7E1]">{t.attendees_count}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase border rounded ${STATUS_CLASS[t.status] || ''}`}>
                      {STATUS_LABEL[t.status] || t.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[#777] text-xs whitespace-nowrap">{fmt(t.issued_at)}</td>
                  <td className="px-4 py-3 text-[#777] text-xs whitespace-nowrap">{fmt(t.scanned_at)}</td>
                  <td className="px-4 py-3 text-[#777] text-xs whitespace-nowrap">{fmt(t.revoked_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-[10px] text-[#555] mt-4 text-center">
          Derived from VelvetGate ticket records. Click any email for cross-event history.
        </p>

        {/* History card — reuses /api/activity for the 5 non-derivable kinds */}
        <HistoryCard eventId={event_id} />
      </div>
    </div>
  );
}

const HISTORY_KINDS = ['event_edited', 'staff_assigned', 'staff_removed', 'event_archived', 'event_unarchived'];

function HistoryCard({ eventId }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (loaded || loading) return;
    setLoading(true); setError(null);
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.get(`${API}/activity`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { event_id: eventId, limit: 100 },
      });
      const filtered = (res.data?.items || []).filter(i => HISTORY_KINDS.includes(i.kind));
      setItems(filtered);
      setLoaded(true);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load history');
    } finally {
      setLoading(false);
    }
  }, [eventId, loaded, loading]);

  useEffect(() => { if (open) load(); }, [open, load]);

  return (
    <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm mt-6" data-testid="event-history-card">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#0D0D0D] transition-colors"
        data-testid="event-history-toggle"
      >
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-[#C6A66B]" />
          <span className="text-sm font-semibold text-[#EAE7E1]">Event History</span>
          {loaded && (
            <span className="text-[10px] text-[#555] ml-1">
              ({items.length} {items.length === 1 ? 'entry' : 'entries'})
            </span>
          )}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-[#777]" /> : <ChevronDown className="w-4 h-4 text-[#777]" />}
      </button>

      {open && (
        <div className="border-t border-[#111]" data-testid="event-history-body">
          {loading && (
            <div className="px-4 py-6 text-center text-xs text-[#555]" data-testid="event-history-loading">Loading…</div>
          )}
          {!loading && error && (
            <div className="px-4 py-6 text-center text-xs text-[#C44545]" data-testid="event-history-error">{error}</div>
          )}
          {!loading && !error && loaded && items.length === 0 && (
            <div className="px-4 py-8 text-center text-xs text-[#555]" data-testid="event-history-empty">
              No edits, staff changes, or archive transitions recorded yet.
            </div>
          )}
          {!loading && !error && items.length > 0 && (
            <div data-testid="event-history-list">
              {items.map(it => (
                <ActivityFeedItem key={it.id} item={it} showEventTitle={false} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, testId, accent }) {
  return (
    <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-4" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-[#777] font-semibold">{label}</div>
      <div className="text-2xl font-bold mt-1" style={{ color: accent || '#D4AF37' }}>{value}</div>
    </div>
  );
}

function Th({ children, className = '' }) {
  return (
    <th className={`px-4 py-3 text-[10px] uppercase tracking-wider text-[#777] font-semibold ${className}`}>{children}</th>
  );
}
